import { useEffect, useMemo, useState } from "react";
import { Breadcrumbs, Link, Tooltip } from "@mui/material";

import "./NavBar.scss";
import { useNavigate } from "react-router-dom";
import {
  getModuleSelectedId,
  pinOption,
} from "../../services/localStorageService";
import { getModuleOptions } from "../../services/navBarService";
import { useSnackBar } from "../../hooks/useSnackBar";
import {
  addTab,
  clearActiveTab,
  closeTab,
  setActiveTab,
} from "../../redux/features/TabsManager/tabSlice";
import { useDispatch } from "react-redux";
import { AircraftConfiguration } from "../../modules/ConfigurationModule/components/AircraftConfiguration/AircraftConfiguration";
import { PartsConfiguration } from "../../modules/ConfigurationModule/components/PartsConfiguration/PartsConfiguration";
import { LocationsConfigurationComponent } from "../../modules/ConfigurationModule/components/LocationsConfiguration/LocationsConfigurationComponent";
import { environment } from "../../environments/environment";
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import { ArrowLeft } from "@mui/icons-material";
import { MobileModal } from "../MobileModal/MobileModal";
import { OptionsMenu } from "../ContextMenu/OptionsMenu";
import { GlobalSearch } from "../GlobalSearch/GlobalSearch";
import { GlobalSearchButton } from "../GlobalSearch/GlobalSearchButton";
import { GlobalSearchBar } from "../GlobalSearch/GlobalSearchBar";
import { GlobalNavbarActions } from "./GlobalNavbarActions";
import { ModulesMenu } from "../ModulesMenu/ModulesMenu";

const tabMap = {
  "/vendor": {
    title: "Vendor",
    componentKey: "Vendor",
    appIcon: "icon-business",
    appColor: "#E19357"
  },
  "/configuration/aircraft": {
    title: "Aircraft",
    componentKey: "AircraftConfiguration",
    appIcon: "icon-aircraft",
    appColor: "#7E38C4",
  },
  "/configuration/parts": {
    title: "Parts",
    componentKey: "PartsConfiguration",
    appIcon: "icon-warehouse",
    appColor: "#f16f6f",
  },
  "/configuration/locations-configuration": {
    title: "Location Configuration",
    componentKey: "LocationsConfiguration",
    appColor: "rgb(24, 193, 246)",
    appIcon: "icon-locations",
  },
  "/store/inventory-management": {
    title: "Inventory Management",
    componentKey: "InventoryManagement",
    appIcon: "icon-inventory-management",
    appColor: "#29abe2",
  },
  "/store/receiving": {
    title: "Receiving Management",
    componentKey: "ReceivingManagement",
    appIcon: "icon-part_request_icon",
    appColor: "#0077FF",
  },
  "/store/part-request-management": {
    title: "Part Request",
    componentKey: "PartRequest",
    breadcrumbsTitle: "Part Request",
    appIcon: "icon-part_request_icon",
    appColor: "#6ac24f",
  },
  "/supplychain/part-request-management": {
    title: "Part Request Management",
    componentKey: "PartRequestSupplyChain",
    appIcon: "icon-part_request_icon",
    appColor: "#4F79C2",
  },
  "/store/package-intake": {
    title: "Package Intake",
    componentKey: "PackageIntake",
    appIcon: "icon-package-intake",
    appColor: "#4F79C2",
  },
  "/store/part-request-management/new-part-request": {
    title: "New Part Request",
    componentKey: "NewPartRequest",
    appIcon: "icon-part_request_icon",
    appColor: "#6ac24f",
  },
  "/customdashboard": {
    title: "Home",
    componentKey: "CustomDashboard",
    appIcon: "icon-home_icon",
    appColor: "#919ce6",
  },
  "/supplychain": {
    title: "Supply Chain",
    componentKey: "Supply Chain",
    appIcon: "icon-supply-chain",
    appColor: "#F38A00",
  },
  "/store": {
    title: "Stores",
    componentKey: "Stores",
    appIcon: "icon-stores",
    appColor: "#eb9090",
  },
  "/configuration": {
    title: "Configuration",
    componentKey: "Configuration",
    appIcon: "icon-settings",
    appColor: "#7bb9db",
  },
  "/store/inventory-transaction": {
    title: "Manual Entry",
    breadcrumbsTitle: "Inventory Transaction",
    componentKey: "InventoryTransaction",
    appIcon: "icon-issue",
    appColor: "#4F79C2",
  },
  "/supplychain/orders-management": {
    title: "Orders Management",
    componentKey: "OrdersManagement",
    appIcon: "icon-part_request_icon",
    appColor: "#0077FF",
  },
  "/supplychain/quote-tracking": {
    title: "Quote Tracking",
    componentKey: "QuoteTrack",
    appIcon: "icon-quote-track",
    appColor: "#25C2C0",
  },
  "/vendor/awb": {
    title: "AWB Vendor",
    componentKey: "VendorAwb",
    appIcon: "icon-awb",
    appColor: "#C5B100"
  },
  "/configuration/engineering-task": {
    title: "Engineering Task",
    componentKey: "EngineeringTask",
    appIcon: "icon-picklist-magements",
    appColor: "#40A1A1"
  },
  "/vendor/discrepancy": {
    title: "Vendor Discrepancy Management",
    componentKey: "Vendor Discrepancy",
    appIcon: "icon-warning",
    appColor: "#F85858"
  },
  "/vendor/vendor-approval": {
    title: "Vendor Approval",
    componentKey: "VendorApproval",
    appIcon: "icon-success",
    appColor: "#E19357",
  },
  "/quote-entry": {
    title: "Quote Entry",
    componentKey: "QuoteEntry",
    appIcon: "icon-success",
    appColor: "#E19357",
  },
  "/supplychain/discrepancy": {
    title: "Discrepancy",
    componentKey: "Discrepancy",
    appIcon: "icon-success",
    appColor: "#E19357",
  },
};
export const NavBar = ({
  dataBreadCrumbs,
  moduleId,
  children,
  previousPath,
  dynamicTitle,
  currentPath,
  moduleOptions,
  hiddeBackButton = false,
  goBackData,
  needClearTab = false,
  moduleMenuProps,
  searchActions,
  screenActions,
  hideBreadcrumbs = false,
  isLocalSearch,
  searchInputProps,
  secondaryActions,
  totalRecords = 0,
  displayRecordsCount = false,
}) => {
  const navigate = useNavigate();
  const [showStoreOptions, setShowStoreOptions] = useState(false);
  const [menuOptions, setMenuOptions] = useState([]);
  const dispatch = useDispatch();
  const { successMsg, infoMsg, warningMsg, errorMsg } = useSnackBar();
  const breadcrumbs = useMemo(
    () => generateBreadcrumbs(dataBreadCrumbs),
    [dataBreadCrumbs]
  );
  const moduleIcon = useMemo(
    () => tabMap[breadcrumbs[0]]?.appIcon,
    [breadcrumbs]
  );
  const moduleColor = useMemo(
    () => tabMap[breadcrumbs[breadcrumbs.length - 1]]?.appColor,
    [breadcrumbs]
  );
  const title = useMemo(
    () => dynamicTitle || tabMap[breadcrumbs[breadcrumbs.length - 1]]?.title,
    [breadcrumbs]
  );
  const menuTitle = useMemo(() => tabMap[breadcrumbs[0]]?.title, [breadcrumbs]);

  useEffect(() => {
    getModuleOptions(moduleId.toString())
      .then((response) => {
        if (response.data.responseCode === 200) {
          setMenuOptions(
            response.data.load.map((option) => ({
              ...option,
              url: "/secure" + option.url,
            }))
          );
        }
      })
      .catch((error) => console.error(error));
  }, [moduleId]);

  const visualizeStoreOptions = () => {
    setShowStoreOptions(true);
  };

  const hiddeStoreOptions = () => {
    setShowStoreOptions(false);
  };
  function generateUUID() {
    return crypto.randomUUID();
  }

  const handleRedirection = (path, icon, color, id) => {
    console.warn("navigating", path)
    window.history.pushState(null, "", path);
    const match = tabMap[path.split("/secure")[1]];
    if (!match) {
      return;
    }

    setShowStoreOptions(false);

    dispatch(
      addTab({
        icon,
        color,
        path: path.split("/secure")[1],
        id,
        title: match.title,
        isPinned: false,
        componentKey: match.componentKey,
        isModule: false,
        date: generateUUID(),
      })
    );
    dispatch(setActiveTab(path));
  };

  const redirectModule = (module) => {
    console.warn("redirecting to module", module);
    let [path, icon, color, id] = [
      `/secure${module.url}`,
      module.icon,
      module.color,
      module.id,
    ];
    handleRedirection(path, icon, color, id);
  };

  const handlePinView = (option) => {
    const info = {
      id: option.id,
      viewName: option.optionName,
      accessPath: option.redirectionPath,
      parentModule: "Store Module",
      iconName: "undefined",
    };
    pinOption(info);
  };

  const clearTabsAndNavigate = (path) => {
    dispatch(clearActiveTab());
    navigate(path);
  };

  // const handleBack = () => {
  //   const basePath = environment.basePath
  //   if (currentPath && previousPath) {

  //     dispatch(setActiveTab(previousPath));
  //     dispatch(clearActiveTab(currentPath))
  //     dispatch(closeTab(currentPath));

  //     navigate(basePath + previousPath);

  //   } else {
  //     throw new Error("No current path provided");
  //   }
  // }

  const handleBack = () => {
    if (goBackData) {
      try {
        if (needClearTab) {
          dispatch(closeTab(currentPath));
        }
        dispatch(addTab(goBackData));
      } catch (error) {
        console.error(error);
      }
    } else {
      throw new Error("No goBackData provided");
    }
  };

  const [mobileHidden, setMobileHidden] = useState(true);

  return (
    <nav className="nav-bar-component">
      <div className="header-n-actions">
        <div className="nav-leftSide">
          <ModulesMenu
            handlerRedirectionModule={(module) => redirectModule(module)}
          >
            <i className="icon-apps module-selector"></i>
          </ModulesMenu>

          <div
            className={`module-navbar-actions btncount-${
              [
                !hiddeBackButton,
                menuOptions?.length > 0,
                moduleOptions?.length > 0,
              ].filter(Boolean).length
            } ${mobileHidden ? "mobileHidden" : ""}`}
          >
            {!hiddeBackButton && (
              <button
                className={`button-back-navBar-component`}
                onClick={handleBack}
              >
                <i className="icon-arrow-down"></i>
                {/* <ArrowLeft></ArrowLeft> */}
              </button>
            )}
            {menuOptions?.length > 0 && (
              <div
                className={`nav-menu desktop`}
                onMouseEnter={visualizeStoreOptions}
                onMouseLeave={hiddeStoreOptions}
              >
                <i className="icon-default"></i>
                <section
                  className="nav-menu-options"
                  id={`${showStoreOptions ? "visualize-store-options" : ""}`}
                  onMouseEnter={visualizeStoreOptions}
                  onMouseLeave={hiddeStoreOptions}
                >
                  <div className="options-icon-container">
                    <i className={moduleIcon}></i>

                    <h2 className="ellipsis">{menuTitle}</h2>
                  </div>
                  <section className="moduleOptions">
                    {menuOptions?.map((option, index) => {
                      return (
                        option && (
                          <div key={index} className="navbar-option">
                            <span
                              style={{ background: option.color }}
                              className="option-icon"
                              onClick={() => {
                                handleRedirection(
                                  option.url,
                                  option.icon,
                                  option.color,
                                  option.id
                                );
                              }}
                            >
                              <i className={option.icon}></i>
                            </span>
                            <p>{option.name}</p>
                          </div>
                        )
                      );
                    })}
                  </section>
                </section>
              </div>
            )}

            {menuOptions?.length > 0 && (
              <div
                className={`nav-menu mobile`}
                onClick={() => setShowStoreOptions((prev) => !prev)}
              >
                <i className="icon-default"></i>
                <MobileModal open={showStoreOptions}>
                  {/* <section
                    className="nav-menu-options"
                    id={`${showStoreOptions ? "visualize-store-options" : ""}`}
                    onMouseEnter={visualizeStoreOptions}
                    onMouseLeave={hiddeStoreOptions}
                  > */}
                  <div className="options-icon-container">
                    <i className={moduleIcon}></i>

                    <h2 className="ellipsis">{menuTitle}</h2>
                  </div>
                  <section className="moduleOptions">
                    {menuOptions?.map((option, index) => {
                      return (
                        option && (
                          <div key={index} className="navbar-option">
                            <span
                              style={{ background: option.color }}
                              className="option-icon"
                              onClick={() => {
                                handleRedirection(
                                  option.url,
                                  option.icon,
                                  option.color,
                                  option.id
                                );
                              }}
                            >
                              <i className={option.icon}></i>
                            </span>
                            <p>{option.name}</p>
                          </div>
                        )
                      );
                    })}
                  </section>
                  {/* </section> */}
                </MobileModal>
              </div>
            )}

            {moduleOptions?.length > 0 && (
              <OptionsMenu options={moduleOptions}>
                <div className="nav-menu">
                  <i className="icon-menu"></i>
                </div>
              </OptionsMenu>
            )}
          </div>

          {/* {moduleOptions?.length > 0 && */}

          {/* <section className='nav-bar-title'> */}
          <div
            className="app-square"
            style={{ background: moduleColor ?? "" }}
            onClick={() => setMobileHidden((prev) => !prev)}
          >
            <i
              className={`${
                tabMap[
                  typeof dataBreadCrumbs === "string"
                    ? dataBreadCrumbs
                    : "/customdashboard"
                ]?.appIcon ??
                moduleIcon ??
                "icon-windows"
              } main-icon`}
            />
          </div>

          <div className="module-title">
            <h3 className="size-subtitle">{title}</h3>
          </div>
          {displayRecordsCount && (
            <Tooltip title={totalRecords + " Records"} placement="bottom" arrow>
              <div className="module-total-records-container">
                <b className="module-total-records-container-text">
                  {totalRecords}
                </b>
              </div>
            </Tooltip>
          )}
          {/* </section> */}
        </div>

        {/* Global Search */}
        <GlobalSearchBar
          searchInputProps={searchInputProps}
          searchActions={searchActions}
          isLocalSearch={isLocalSearch}
        />

        <GlobalNavbarActions screenActions={screenActions} />
        {/**navbar actions */}
        {children}
      </div>
      {!hideBreadcrumbs && <div className="breadcrumbs-container">
        <Breadcrumbs separator=">">
          <Link
            key={1}
            underline="hover"
            sx={{
              color: "#000000",
              cursor: "pointer",
              fontSize: "var(--p-size)",
            }}
            onClick={() => clearTabsAndNavigate("/secure/customdashboard")}
          >
            Modules
          </Link>
          {breadcrumbs.map((path) => (
            <Link
              key={2}
              underline="hover"
              sx={{
                color: "#000000",
                cursor: "pointer",
                fontSize: "var(--p-size)",
              }}
              onClick={() => clearTabsAndNavigate("/secure" + path)}
            >
              {tabMap[path]?.breadcrumbsTitle ?? tabMap[path]?.title}
            </Link>
          ))}
        </Breadcrumbs>
        {secondaryActions && secondaryActions}
      </div>}
    </nav>
  );
};

const generateBreadcrumbs = (path) => {
  return (typeof path === "string" ? path : "/customdashboard")
    .split("/")
    .filter(Boolean)
    .map((_, i, arr) => "/" + arr.slice(0, i + 1).join("/"));
};
